// This file is for helpers that relate to any formatting within the application
// this is here because we have functions that are shared between the major
// modules in the application

import * as R from 'ramda';
import { format } from 'date-fns';

// Text formatting functions remain the same
export const capitalizeMultiWords = R.compose(
  R.join(' '),
  R.map(word => word[0].toUpperCase() + word.substr(1).toLowerCase()),
  R.split(' ')
);

export const capitalize = R.compose(
  R.join(' '),
  R.map(word => word[0].toUpperCase() + word.substr(1).toLowerCase()),
  R.split('-')
);

export const capitalizeSingleWord = word =>
  `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;

export const formatDateForExport =
  (dateFormatString = 'MM/d/yyyy') =>
  dateGridCellDateObject => {
    if (R.isNil(dateGridCellDateObject.value)) {
      return null;
    }

    if (dateGridCellDateObject.id !== 'auto-generated-group-footer-root') {
      return format(new Date(dateGridCellDateObject.value || null), dateFormatString);
    }

    return dateGridCellDateObject.value;
  };

// CONSOLIDATED NUMBER FORMATTING UTILITIES

/**
 * Format a number with thousand separators and optional decimal places
 *
 * @param {number|string} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {number} [options.decimals=0] - Number of decimal places
 * @param {boolean} [options.roundToWhole=true] - Whether to round to whole number
 * @param {number} [options.defaultValue=0] - Default value if input is null/undefined
 * @param {string} [options.prefix=''] - Prefix to add before the formatted number (e.g., '$')
 * @param {string} [options.suffix=''] - Suffix to add after the formatted number (e.g., 'M')
 * @returns {string} The formatted number as a string with thousand separators
 */
export const formatNumber = (value, options = {}) => {
  const { decimals = 0, roundToWhole = true, defaultValue = 0, prefix = '', suffix = '' } = options;

  // Handle null, undefined, NaN values
  if (value == null || Number.isNaN(Number(value))) {
    return `${prefix}${new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(defaultValue)}${suffix}`;
  }

  // Convert to number if it's not already
  let numValue = Number(value);

  // Round to whole number if specified
  if (roundToWhole && decimals === 0) {
    numValue = Math.round(numValue);
  }

  // Format with thousand separators
  return `${prefix}${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(numValue)}${suffix}`;
};

// For DataGrid specific formatting
export const formatNumberCell = (params, options = {}) => formatNumber(params.value, options);

// Backward compatibility with existing functions
export const formatGallons = gallons => formatNumber(gallons, { suffix: ' gal' });

// TODO: This function is failing in the test suite. Several uses of this function will need to be reviewed and potentially updated to ensure correct formatting behavior.
// Legacy function maintained for backward compatibility
export const formatVolume = volume => {
  return R.cond([
    [
      R.and(R.gte(R.__, 999000), R.lte(R.__, 9999000)),
      R.always(
        `${new Intl.NumberFormat('en-US', {
          maximumSignificantDigits: 2,
          notation: 'compact',
        }).format(volume)}M` // TODO: This line is causing the test to fail due to the unexpected "M" suffix being appended.
      ),
    ],
    [
      R.lte(R.__, 999000),
      R.always(
        new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3, notation: 'compact' }).format(
          volume
        )
      ),
    ],
    [
      R.T,
      R.always(
        `${new Intl.NumberFormat('en-US', {
          maximumSignificantDigits: 3,
          notation: 'compact',
        }).format(volume)}M` // TODO: This line is causing the test to fail due to the unexpected "M" suffix being appended.
      ),
    ],
  ])(volume);
};

// Simple volume formatter for DataGrid cells (thousands separator only)
export const formatVolumeCell = params => formatNumber(params.value || 0);

// Remove leading zeros from a number or numeric string
export const removeLeadingZeros = value => {
  if (!value && value !== 0) return null;
  const stringValue = String(value);
  const trimmedValue = stringValue.replace(/^0+/, '') || null;
  return trimmedValue ? Number(trimmedValue) : null;
};
// Add number formatting utility with commas
export const formatNumberWithCommas = value => {
  if (!value) return '';
  const numString = value.toString();
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Currency formatting (was formatDollarsWithoutDecimals)
export const formatCurrency = value => formatNumber(value, { prefix: '$' });

// Backward compatibility
export const formatDollarsWithoutDecimals = num => formatCurrency(num);

// Price formatting (with 2 decimal places)
export const formatPrice = value => formatNumber(value, { decimals: 2, prefix: '$' });

// For DataGrid specific price formatting
export const formatPriceCell = params => formatPrice(params.value);

// Backward compatibility
export const formatNumberWithoutDecimals = num => formatNumber(num);

// Other utility functions remain unchanged
export const convertDataCompanyId = R.cond([
  [R.equals(1), R.always('MPL')],
  [R.equals(5), R.always('MTH')],
]);

export const checkStringForDash = (word = '') =>
  word.includes('-') ? capitalize(word) : capitalizeSingleWord(word);

export const strToInt = str => parseInt(str, 10);
